import type { Theme, Direction, CommonColors, ThemeProviderProps } from '@mui/material/styles';
import type { ThemeCssVariables } from './types';
import type { PaletteColorKey, PaletteColorNoChannels } from './core/palette';

// ----------------------------------------------------------------------

export type ThemeConfig = {
  direction: Direction;
  classesPrefix: string;
  cssVariables: ThemeCssVariables;
  defaultMode: ThemeProviderProps<Theme>['defaultMode'];
  modeStorageKey: ThemeProviderProps<Theme>['modeStorageKey'];
  fontFamily: Record<'primary' | 'secondary', string>;
  palette: Record<PaletteColorKey, PaletteColorNoChannels> & {
    common: Pick<CommonColors, 'black' | 'white'>;
    grey: {
      [K in 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 as `${K}`]: string;
    };
  };
};

export const themeConfig: ThemeConfig = {
  /** **************************************
   * Base
   *************************************** */
  defaultMode: 'light',          // light default — user toggles in settings
  modeStorageKey: 'theme-mode',  // persists user preference in localStorage
  direction: 'ltr',
  classesPrefix: 'minimal',

  /** **************************************
   * CSS variables
   *************************************** */
  cssVariables: {
    cssVarPrefix: '',
    colorSchemeSelector: 'data-color-scheme',
  },

  /** **************************************
   * Typography
   * DM Sans Variable → body / UI text
   * DM Serif Display → headings (h1–h5)
   *************************************** */
  fontFamily: {
    primary:   'DM Sans Variable',
    secondary: 'DM Serif Display',
  },

  /** **************************************
   * Palette — cmpr Design Language
   *
   * Primary:  Cobalt accent (#1a4fd6)
   * Grey:     Warm stone neutrals
   *
   * Dark mode uses true-black backgrounds
   * (#0e0e0e page, #111111 surfaces) with
   * cobalt staying the accent throughout.
   *************************************** */
  palette: {

    // ── Cobalt — single brand accent ──────────────────────────────────
    primary: {
      lighter:      '#dce6fb',
      light:        '#5a85e8',
      main:         '#1a4fd6',
      dark:         '#1239a8',
      darker:       '#0d2680',
      contrastText: '#FFFFFF',
    },

    // ── Warm stone — secondary actions ────────────────────────────────
    secondary: {
      lighter:      '#f4f3ee',
      light:        '#a8a39a',
      main:         '#57524c',
      dark:         '#3d3a35',
      darker:       '#252320',
      contrastText: '#FFFFFF',
    },

    // ── Info maps to cobalt ────────────────────────────────────────────
    info: {
      lighter:      '#dce6fb',
      light:        '#5a85e8',
      main:         '#1a4fd6',
      dark:         '#1239a8',
      darker:       '#0d2680',
      contrastText: '#FFFFFF',
    },

    // ── Success ────────────────────────────────────────────────────────
    success: {
      lighter:      '#e4f5ec',
      light:        '#4caf7d',
      main:         '#1a7a46',
      dark:         '#145e36',
      darker:       '#0d3f24',
      contrastText: '#FFFFFF',
    },

    // ── Warning ────────────────────────────────────────────────────────
    warning: {
      lighter:      '#fdf3e0',
      light:        '#e6b34d',
      main:         '#9a6400',
      dark:         '#7a5000',
      darker:       '#573800',
      contrastText: '#FFFFFF',
    },

    // ── Error ──────────────────────────────────────────────────────────
    error: {
      lighter:      '#fde8e8',
      light:        '#e06b60',
      main:         '#c0392b',
      dark:         '#962d21',
      darker:       '#6b1f17',
      contrastText: '#FFFFFF',
    },

    // ── Warm stone neutrals ────────────────────────────────────────────
    // Used for light mode backgrounds, borders, text hierarchy.
    // In dark mode the grey scale is effectively inverted —
    // grey[900] becomes the page bg, grey[50] becomes primary text.
    grey: {
      50:  '#fafaf7',
      100: '#f4f3ee',
      200: '#e8e6df',
      300: '#d4d0c8',
      400: '#a8a39a',
      500: '#7a746c',
      600: '#57524c',
      700: '#3d3a35',
      800: '#252320',
      900: '#151412',
    },

    common: {
      black: '#000000',
      white: '#FFFFFF',
    },
  },
};
